<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';

import useTrackingLinksStore from '@/stores/tracking-links';

import { withCommas } from '@/utils/numbers';

import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Card from 'primevue/card';
import FloatLabel from 'primevue/floatlabel';
import SelectButton from 'primevue/selectbutton';
import Skeleton from 'primevue/skeleton';

import Metadata from '@/components/Analytics/TrackingLinks/Metadata.vue';
import ViewsOverTime from '@/components/Analytics/TrackingLinks/ViewsOverTime.vue';
import ViewsMap from '@/components/Analytics/TrackingLinks/ViewsMap.vue';
import ViewsPieChart from '@/components/Analytics/TrackingLinks/ViewsPieChart.vue';

const $router = useRouter();
const $route = useRoute();

const trackingLinkId = computed(() => $route.params.id);

const {
  isFetching,
  isExporting,
  trackingLink,
  analytics,
} = storeToRefs(useTrackingLinksStore());

const {
  fetchTrackingLink,
  fetchAnalytics,
  exportVisits,
} = useTrackingLinksStore();

const isExportingSlow = ref(false);

const page = computed(() => trackingLink?.value?.currentDestination?.landingPage);

const totalVisits = computed(() => withCommas(analytics.value?.visits?.total));
const uniqueVisits = computed(() => withCommas(analytics.value?.visits?.unique));

const startDate = ref(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
const endDate = ref(new Date());

const interval = +$route.query.interval;

// Initialize using query param(s) if available,
// otherwise default to past 30 days for charts
if (interval >= 0) {
  // Set dates based on preset interval in query param.
  // Where interval is number of days, relative from today.
  startDate.value = interval === 0 ? null : new Date(Date.now() - (interval * 24 * 60 * 60 * 1e3));
} else if ($route.query.startDate) {
  startDate.value = new Date($route.query.startDate);
  endDate.value = new Date($route.query.endDate || new Date());
}

const defaultRange = $route.query.interval || (
  ($route.query.startDate || $route.query.endDate)
    ? -1
    : 30
);

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const rangeButtonsValue = ref(+defaultRange);
const showCustomControls = ref(+defaultRange === -1);

// Max date is today plus one day, that way today's stats show up properly if selected.
const maxDate = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + 1); // Add one day to today
  return date;
});

/**
 * Update query params to reflect current range.
 * If days is set to -1, then we'll use startDate and endDate params.
 * Otherwise we'll use `interval` since that is relative to now.
 * @param {number} days - Number of days worth of data to pull, relative to today.
 */
const updateRangeQueryParams = (days) => {
  const query = {};

  if (days === -1) {
    query.startDate = startDate.value ? startDate.value.toISOString() : '';
    query.endDate = endDate.value ? endDate.value.toISOString() : '';
  } else {
    query.interval = days;
  }

  $router.replace({ query });
};

const selectedCustomDates = computed({
  get() {
    return [startDate.value || new Date(), endDate.value];
  },

  set(val) {
    [startDate.value, endDate.value] = val;

    // Set end date to the EOD for selected day, instead of start
    endDate.value.setHours(23, 59, 59, 999); // Set to end of day

    updateRangeQueryParams(-1);

    if (endDate.value) {
      // Wait for end date to be set before making a request
      fetchAnalytics(trackingLinkId.value, {
        ...(startDate.value ? { startDate: startDate.value } : {}),
        endDate: endDate.value,
        timezone,
      });
    }
  },
});

const rangePickerOptions = ref([
  {
    label: '24 Hours',
    value: 1,
    dataEntity: 'analytics-trackingLink-rangeButtons-24Hours',
  },
  {
    label: '7 Days',
    value: 7,
    dataEntity: 'analytics-trackingLink-rangeButtons-7Days',
  },
  {
    label: '30 Days',
    value: 30,
    dataEntity: 'analytics-trackingLink-rangeButtons-30Days',
  },
  {
    label: '90 Days',
    value: 90,
    dataEntity: 'analytics-trackingLink-rangeButtons-90Days',
  },
  {
    label: 'All Time',
    value: 0,
    dataEntity: 'analytics-trackingLink-rangeButtons-allTime',
  },
  {
    label: 'Custom',
    value: -1,
    dataEntity: 'analytics-trackingLink-rangeButtons-allTime',
  },
]);

/**
 * Called when user switches date range.
 * @param {number} days - Number of days worth of data to pull, relative to today.
 */
const onUpdateRange = (days) => {
  updateRangeQueryParams(days); // Using query params so analytics links are more shareable

  if (days === -1) {
    showCustomControls.value = true;
    return;
  }

  showCustomControls.value = false;

  if (days === 0) {
    // "All Time" stats, so we just clear startDate
    startDate.value = null;
  } else {
    // Set start and end dates based on selected range
    startDate.value = new Date(Date.now() - (days * 24 * 60 * 60 * 1000));
  }

  // Reset endDate to current date, jic some user is sitting on the page forever.
  endDate.value = new Date();

  // Fetch analytics data for the new date range
  fetchAnalytics(trackingLinkId.value, {
    ...(startDate.value ? { startDate: startDate.value } : {}),
    endDate: endDate.value,
    timezone,
  });
};

let slowExportTimeout;

const onClickExport = () => {
  isExportingSlow.value = false;

  // If takes longer than 3s, then show additional warning about
  // how exporting can take a bit of time.
  clearTimeout(slowExportTimeout);
  slowExportTimeout = setTimeout(() => {
    isExportingSlow.value = true;
  }, 3e3);

  exportVisits(trackingLinkId.value, {
    ...(startDate.value ? { startDate: startDate.value } : {}),
    endDate: endDate.value,
    timezone,
  }).finally(() => {
    clearTimeout(slowExportTimeout);
    isExportingSlow.value = false;
  });
};

fetchTrackingLink(trackingLinkId.value);
fetchAnalytics(trackingLinkId.value, {
  ...(startDate.value ? { startDate: startDate.value } : {}),
  endDate: endDate.value,
  timezone,
});
</script>

<template>
  <div class="px-6" data-test="analytics-trackingLink">
    <div class="mb-3 flex align-items-center gap-5">
      <h2 class="m-0">
        <router-link
          to="/pages"
          data-entity="analytics-trackingLink-backButton"
        >
          <font-awesome-icon
            class="cursor-pointer mr-3"
            role="button"
            aria-label="Go back"
            v-tooltip="'Go back'"
            title="Go back"
            icon="far fa-arrow-left"
          />
        </router-link>

        {{ page ? 'Page Analytics' : 'Tracking Link Analytics' }}
      </h2>
    </div>

    <Metadata />

    <div class="flex align-items-center gap-3 mb-3">
      <!-- Date Range Controls -->
      <!-- This effects ALL data rendered below -->
      <SelectButton
        v-model="rangeButtonsValue"
        :options="rangePickerOptions"
        optionValue="value"
        optionLabel="label"
        aria-labelledby="basic"
        data-key="value"
        @update:modelValue="onUpdateRange"
        :allowEmpty="false"
        data-test="analytics-embed-rangeButtons"
        data-entity="analytics-embed-rangeButtons"
        :pt="{ button: ({ context }) => ({ 'data-entity': context.option.dataEntity }) }"
      />

      <FloatLabel v-if="showCustomControls">
        <Calendar
          v-model="selectedCustomDates"
          inputId="date1"
          selectionMode="range"
          :manualInput="false"
          data-test="analytics-trackingLink-calendarPicker"
          data-entity="analytics-trackingLink-calendarPicker"
          :maxDate="maxDate"
        />
        <label class="analytics-dateRange-label" for="date1">Date Range</label>
      </FloatLabel>

      <div class="ml-auto flex align-items-center">
        <div
          v-if="isExporting && isExportingSlow"
          class="mr-2 flex flex-column text-xs text-right"
        >
          <span class="font-bold">
            Large exports may take a few minutes.
          </span>

          <span>
            Don't close or refresh this tab.
          </span>
        </div>

        <!-- Export CSV Button -->
        <Button
          type="button"
          :label="isExporting ? 'Exporting...' : 'Export visits (CSV)'"
          :aria-label="isExporting ? 'Exporting...' : 'Export visits (CSV)'"
          data-test="analytics-trackingLink-exportCsv"
          data-entity="analytics-trackingLink-exportCsv"
          :disabled="isExporting"
          @click="onClickExport"
        >
          <template #icon>
            <FontAwesomeIcon
              :icon="isExporting ? 'far fa-spinner-third' : 'fas fa-file-csv'"
              :spin="isExporting"
              class="mx-2"
            />
          </template>
        </Button>
      </div>
    </div>

    <!-- Visit Analytics -->
    <div class="flex items-center justify-center gap-3 mb-3">
      <Card pt:root:class="analytics-card analytics-card__total-visits shadow-2">
        <template #content>
          <div v-if="isFetching" class="flex flex-column justify-content-center align-items-center">
            <Skeleton height="1rem" width="6rem" class="mb-2" />
            <Skeleton height="4rem" width="4rem" />
          </div>

          <Transition name="fade-in-out">
            <div v-if="!isFetching">
              <div class="flex flex-column align-items-center justify-content-center text-center">
                <strong>Total Visits</strong>
                <span class="text-6xl font-bold unit">{{ totalVisits }}</span>
              </div>
            </div>
          </Transition>
        </template>
      </Card>

      <Card pt:root:class="analytics-card analytics-card__unique-visits shadow-2">
        <template #content>
          <div v-if="isFetching" class="flex flex-column justify-content-center align-items-center">
            <Skeleton height="1rem" width="6rem" class="mb-2" />
            <Skeleton height="4rem" width="4rem" />
          </div>

          <Transition name="fade-in-out">
            <div v-if="!isFetching">
              <div class="flex flex-column align-items-center justify-content-center text-center">
                <strong>Unique Visits</strong>
                <span class="text-6xl font-bold unit">{{ uniqueVisits }}</span>
              </div>
            </div>
          </Transition>
        </template>
      </Card>
    </div>
    <!-- End Visit Analytics -->

    <ViewsOverTime
      :start-date="startDate"
      :end-date="endDate"
    />

    <!-- Visits by Location -->
    <div class="flex items-center justify-center gap-3 mb-3">
      <ViewsMap dataset="total" />
      <ViewsMap dataset="unique" />
    </div>
    <!-- End Visits by Location -->

    <!-- Visits by Browser/OS/Device -->
    <div class="flex items-center justify-center gap-3 mb-3">
      <ViewsPieChart label="Browser" dataset="browser" />
      <ViewsPieChart label="OS" dataset="os" />
      <ViewsPieChart label="Device" dataset="device" />
    </div>
    <!-- End Visits by Browser/OS/Device -->
  </div>
</template>

<style lang="scss">
.analytics-card {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .p-card-content {
    padding: 0;
  }

  .p-datatable-wrapper {
    min-height: 300px;
  }

  &__total-visits {
    .unit {
      color: var(--primary-900);
      .dark-theme & {
        color: var(--primary-300)
      }
    }
  }

  &__unique-visits {
    .unit {
      color: var(--secondary-600);
      .dark-theme & {
        color: var(--secondary-200)
      }
    }
  }
}

.analytics-card__metadata-item {
  overflow: hidden;
  min-width: 20%;
  max-width: 250px;

  .analytics-card__metadata-item-value {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.mapboxgl-control-container {
  display: none;
}

.analytics-card__table-column-small {
  width: 200px;
  text-align: center;

  > .p-column-header-content {
    justify-content: center;
  }
}
</style>
